// ============================================================================
// BodySection — body trends: weight / body-fat / BMI before→after deltas plus
// animated trend charts. Each trend chart renders only when its series has ≥2
// points; each delta chip only when the delta exists.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components";
import { IconArrowDown, IconArrowUp, IconMinus } from "../../../components/icons";
import { LineChart } from "../charts/LineChart";
import type { ChartAccent } from "../charts/LineChart";
import { ReportSection } from "./ReportSection";
import type { BodyMetricKey, TypedClientReport } from "../types";
import { fmtNum, fmtSigned, metricDecimals, metricUnit } from "../format";

const DELTA_KEYS: BodyMetricKey[] = ["weight", "body_fat", "bmi", "whr", "whtr"];

/** Chart accent per body metric — keeps each trend visually distinct. */
const METRIC_ACCENT: Record<BodyMetricKey, ChartAccent> = {
  weight: "coral",
  body_fat: "violet",
  bmi: "ocean",
  whr: "lime",
  whtr: "lime",
};

/** For most body metrics, a DROP is the win (green). Direction-aware tone. */
function deltaTone(key: BodyMetricKey, change: number): "up" | "down" | "flat" {
  const rounded = Number(change.toFixed(metricDecimals(key)));
  if (rounded === 0) return "flat";
  // weight / fat / bmi / ratios: lower is generally better → down-change = good.
  return rounded < 0 ? "up" : "down";
}

function DeltaCard({
  metricKey,
  before,
  after,
  change,
}: {
  metricKey: BodyMetricKey;
  before: number;
  after: number;
  change: number;
}) {
  const { t, i18n } = useTranslation("clientReport");
  const locale = i18n.language;
  const dec = metricDecimals(metricKey);
  const unit = metricUnit(metricKey);
  const tone = deltaTone(metricKey, change);
  const badgeTone = tone === "up" ? "success" : tone === "down" ? "danger" : "neutral";

  const Arrow =
    tone === "up" ? IconArrowDown : tone === "down" ? IconArrowUp : IconMinus;

  return (
    <div className="rounded-lg bg-surface-soft p-4 ring-1 ring-line-soft">
      <p className="font-display text-sm font-bold text-body-soft">
        {t(`metrics.${metricKey}`)}
      </p>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="flex items-baseline gap-1.5">
          <span className="nums font-display text-2xl font-extrabold tracking-tightest text-body">
            {fmtNum(after, dec, locale)}
          </span>
          <span className="text-xs font-bold text-body-faint">{unit.trim()}</span>
        </div>
        <Badge tone={badgeTone} className="mb-0.5">
          <Arrow width={12} height={12} />
          {fmtSigned(change, dec, locale)}
          {unit}
        </Badge>
      </div>
      <p className="mt-1.5 text-xs text-body-faint">
        {t("body.before")} {fmtNum(before, dec, locale)}
        {unit} → {t("body.after")} {fmtNum(after, dec, locale)}
        {unit}
      </p>
    </div>
  );
}

export function BodySection({
  report,
  index,
}: {
  report: TypedClientReport;
  index: number;
}) {
  const { t, i18n } = useTranslation("clientReport");
  const locale = i18n.language;
  const { body } = report;

  const deltas = DELTA_KEYS.map((k) => ({ k, d: body.deltas[k] })).filter(
    (x): x is { k: BodyMetricKey; d: NonNullable<typeof x.d> } => Boolean(x.d),
  );

  // Trend charts worth drawing (need ≥2 points). Weight + body-fat are the
  // headline trends the trainer wants front and centre.
  const trendKeys: BodyMetricKey[] = (["weight", "body_fat", "bmi"] as const).filter(
    (k) => (body.series[k]?.length ?? 0) >= 2,
  );

  return (
    <ReportSection
      id="report-body"
      index={index}
      kicker={t("kicker")}
      title={t("body.title")}
      subtitle={t("body.subtitle")}
      hasData={body.ok}
    >
      {deltas.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {deltas.map(({ k, d }) => (
            <DeltaCard
              key={k}
              metricKey={k}
              before={d.before}
              after={d.after}
              change={d.change}
            />
          ))}
        </div>
      )}

      <div className="space-y-6">
        {trendKeys.map((k) => (
          <div key={k}>
            <h3 className="mb-2 font-display text-sm font-bold text-body-soft">
              {t("body.trendOf", { metric: t(`metrics.${k}`) })}
            </h3>
            <LineChart
              series={body.series[k]}
              accent={METRIC_ACCENT[k]}
              decimals={metricDecimals(k)}
              ariaLabel={t("body.trendOf", { metric: t(`metrics.${k}`) })}
              locale={locale}
            />
          </div>
        ))}
      </div>
    </ReportSection>
  );
}
